import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { filter, map, tap, withLatestFrom } from "rxjs/operators";

import { CurrencyService } from "@hermes/aphrodite/price";
import { VtoProduct } from "@hermes/api-model-virtual-try-on";
import { COUNTRY, LOCALE } from "@hermes/app-core";
import { Country, Locale } from "@hermes/locale";
import { addToCartSuccess } from "@hermes/states/basket";
import { ProductPageFacade } from "@hermes/states/product-page";
import { AnalyticsService } from "@hermes/utils/analytics";

import {
    catchSdkError,
    learnMoreRedirect,
    openModal,
} from "../actions/vto.actions";

import { SINGLE_PRODUCT_PAGE } from "../constants/vto.constant";
import { LearnMoreClickEvent } from "../events/learn-more-click.event";
import { VtoAddToCartEvent } from "../events/vto-add-to-cart.event";
import { VtoButtonClickAnalyticsEvent } from "../events/vto-button-click.event";
import { VtoErrorAnalyticsEvent } from "../events/vto-error.event";
import { VtoPageViewAnalyticsEvent } from "../events/vto-page-view.event";
import { VtoFacade } from "../facades/vto.facade";
import {
    getProductIndexfromList,
    getProductVariantFromList,
    isVtoProductNotUndefined,
} from "../helpers/vto.helper";

@Injectable()
export class VtoEffectsAnalytics {
    public sendOpenModalEvents$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(openModal),
                withLatestFrom(this.productPageFacade.currentProduct$),
                filter((currentProduct) => !!currentProduct),
                tap(([action, currentProduct]) => {
                    this.analyticsService.sendData(
                        new VtoButtonClickAnalyticsEvent({
                            pagetype: action.pageType,
                            eventLabel: action.eventLabel,
                        }),
                    );
                    this.analyticsService.sendData(
                        new VtoPageViewAnalyticsEvent({
                            pagetype: action.pageType,
                            virtualpageuri:
                                action.pageType === SINGLE_PRODUCT_PAGE
                                    ? currentProduct?.url
                                    : this.locale.urlPrefix +
                                      this.router.url.replace("#|", ""),
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    public sendLearnMoreEvent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(learnMoreRedirect),
                map((action) => {
                    if (!action.vtoCategory) {
                        return { vtoProduct: undefined, position: -1 };
                    }
                    const vtoProduct = getProductVariantFromList(
                        action.vtoSubcategory
                            ? action.vtoSubcategory?.vtoProducts
                            : [],
                        action.vtoSubcategory?.selectedSkuVariant,
                    );
                    const position = getProductIndexfromList(
                        action.vtoSubcategory
                            ? action.vtoSubcategory?.vtoProducts
                            : [],
                        action.vtoSubcategory?.selectedSkuVariant,
                    );

                    return {
                        vtoProduct,
                        position,
                    };
                }),
                filter(
                    (vtoProductAndIndex: {
                        vtoProduct: VtoProduct | undefined;
                        position: number;
                    }): vtoProductAndIndex is {
                        vtoProduct: VtoProduct;
                        position: number;
                    } =>
                        isVtoProductNotUndefined(vtoProductAndIndex.vtoProduct),
                ),
                tap(
                    ({
                        vtoProduct,
                        position,
                    }: {
                        vtoProduct: VtoProduct;
                        position: number;
                    }) => {
                        this.analyticsService.sendData(
                            new LearnMoreClickEvent({
                                product: vtoProduct,
                                index: position,
                                currency: this.country.currency,
                            }),
                        );
                    },
                ),
            ),
        { dispatch: false },
    );

    public sendAnalyticsError$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(catchSdkError),
                withLatestFrom(this.vtoFacade.modalStatus$),
                tap((modalStatusState) => {
                    this.analyticsService.sendData(
                        new VtoErrorAnalyticsEvent({
                            eventType: modalStatusState[0].sdkError,
                        }),
                    );
                }),
            ),
        {
            dispatch: false,
        },
    );

    public vtoAddToCartSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(addToCartSuccess),
                withLatestFrom(this.vtoFacade.vtoProductSelected$),
                tap(([action, vtoProductSelected]) => {
                    if (vtoProductSelected) {
                        this.analyticsService.sendData(
                            new VtoAddToCartEvent({
                                currencyCode:
                                    this.currencyService.getCurrencyMap()?.[1],
                                product: vtoProductSelected,
                                context: action.context,
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        private analyticsService: AnalyticsService,
        private vtoFacade: VtoFacade,
        private productPageFacade: ProductPageFacade,
        private currencyService: CurrencyService,
        @Inject(COUNTRY) public country: Country,
        @Inject(LOCALE) private locale: Locale,
        private router: Router,
    ) {}
}
